import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismCard,
  PrismIcon,
  PrismLoadingBar,
  PrismMetricBlock,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import topSearchingCountriesFormat from 'lumen/helpers/top-searching-countries-format';
import {
  MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
  MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
  MI_SEARCHES_TYPE_HOTELS_BUSINESS,
  MI_SEARCHES_TYPE_HOTELS_LEISURE,
} from 'lumen/utils/constants/general-constants';
import generateMarketInsightRedirectUrlQueryParams from 'lumen/utils/overview-dashboard/generate-market-insight-redirect-url-query-params';

interface HotelOverviewDashboardTilesMarketTrendsTopSearchingCountriesSignature {
  Args: {
    data: unknown;
    isLoading: unknown;
    selectedRange: unknown;
    tileId: unknown;
  };
}

export default class HotelOverviewDashboardTilesMarketTrendsTopSearchingCountries extends Component<HotelOverviewDashboardTilesMarketTrendsTopSearchingCountriesSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare odSelections: Services['od-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasMarketInsightData() {
    // @ts-expect-error: Incorrect type
    return !this.args.data.isEmptyResponse;
  }

  // The redirect link redirects the user to Market Insight (overview or day detail view - depending on the selected date range)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiRedirectUrlQueryParams() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const refDaysAgo = this.odSelections.selectedRefDaysAgo.id;

    // @ts-expect-error: Incorrect type
    if (this.odSelections.isDayDrilldownActive) {
      const activeDrilldownDayMoment = dayjs(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.odSelections.activeDrilldownDayAsString,
      );

      return generateMarketInsightRedirectUrlQueryParams(
        // @ts-expect-error: Incorrect type
        this.args.selectedRange.start,
        // @ts-expect-error: Incorrect type
        this.args.selectedRange.end,
        refDaysAgo,
        // @ts-expect-error: Incorrect type
        activeDrilldownDayMoment,
        null,
        true,
        'searches',
      );
    }

    return generateMarketInsightRedirectUrlQueryParams(
      // @ts-expect-error: Incorrect type
      this.args.selectedRange.start,
      // @ts-expect-error: Incorrect type
      this.args.selectedRange.end,
      refDaysAgo,
      null,
      null,
      true,
      // @ts-expect-error: Incorrect type
      'searches',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get topSearchingCountriesValues() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    let searchSummaries = this.args.data.kpis.searchsummaries;
    const metaOtaTranslation = `${this.intl.t('general.meta')}/${this.intl.t('general.ota')}`;
    const gdsTranslation = this.intl.t('general.GDS');
    const flightsTranslation = this.intl.t('market_insight.flights');
    const hotelsTranslation = this.intl.t('general.hotels');

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const flightsGdsAvailable = searchSummaries.some(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (summary) => summary.type === MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const hotelGdsAvailable = searchSummaries.some(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (summary) => summary.type === MI_SEARCHES_TYPE_HOTELS_BUSINESS,
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    searchSummaries = searchSummaries
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .filter((summary) => summary.type !== MI_SEARCHES_TYPE_HOTELS_BUSINESS)
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .map((summary) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        summary.icon = [
          'plane',
          'business-type-hotel',
          'business-type-hotel',
          'plane',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        ][summary.type];

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (summary.type === MI_SEARCHES_TYPE_FLIGHTS_BUSINESS) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          summary.name = gdsTranslation;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return summary;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (MI_SEARCHES_TYPE_FLIGHTS_LEISURE === summary.type) {
          if (!flightsGdsAvailable && !hotelGdsAvailable) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            summary.name = flightsTranslation;
          } else if (!flightsGdsAvailable) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            summary.name = `${metaOtaTranslation}/${gdsTranslation}`;
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            summary.name = metaOtaTranslation;
          }
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        } else if (MI_SEARCHES_TYPE_HOTELS_LEISURE === summary.type) {
          if (!flightsGdsAvailable && !hotelGdsAvailable) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            summary.name = hotelsTranslation;
          } else if (!hotelGdsAvailable) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            summary.name = `${metaOtaTranslation}/${gdsTranslation}`;
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            summary.name = metaOtaTranslation;
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return summary;
      });

    const sortOrder = [
      MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
      MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
      MI_SEARCHES_TYPE_HOTELS_LEISURE,
      MI_SEARCHES_TYPE_HOTELS_BUSINESS,
    ];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    searchSummaries.sort(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      (a, b) => sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type),
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return searchSummaries;
  }

  <template>
    <PrismCard
      class="prism-card--od-kpi overview-top-searching-countries-block"
    >
      <:header as |Header|>
        <div class="flex items-center gap-100">
          <Header.title>
            {{t "market_insight.top_searching_countries"}}
          </Header.title>
          <GeneralIconWithTooltip
            @side="top"
            @tooltipContent={{t
              "overview_dashboard.top_searching_countries.tooltip"
            }}
          />
        </div>
      </:header>

      <:content>
        <div class="od-kpi-content flex flex-col">
          {{#if @isLoading}}
            <PrismLoadingBar />
          {{else}}
            {{#if this.hasMarketInsightData}}
              <div class="flex flex-col gap-200">
                {{#each this.topSearchingCountriesValues as |searchsummary|}}
                  <div class="overview-top-searching-countries-block__row">
                    <span class="flex items-center gap-100">
                      <PrismIcon @name={{searchsummary.icon}} />
                      {{searchsummary.name}}
                    </span>
                    <span class="text-300--bold">
                      {{topSearchingCountriesFormat
                        searchsummary.top_countries
                      }}
                    </span>
                  </div>
                {{/each}}
              </div>
              <div class="redirect-link-container">
                <LinkTo
                  @model={{this.hotelAndMarketService.hotel.id}}
                  @query={{this.kpiRedirectUrlQueryParams}}
                  @route="hotel.market-insight"
                  class="prism-link prism-link--standalone"
                  data-event-id="hotel_overview-kpi_main_redirect-click"
                  data-event-value={{@tileId}}
                  target="_blank"
                >
                  {{t "overview_dashboard.kpi.link.market_insight"}}
                </LinkTo>
              </div>
            {{else}}
              <PrismMetricBlock @align="left">
                <:value>
                  {{t "general.missing"}}
                </:value>
                <:label>
                  {{t "overview_dashboard.kpi.range_selection"}}
                </:label>
              </PrismMetricBlock>
            {{/if}}
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
